import * as React from "react";

import HCaptcha from "@hcaptcha/react-hcaptcha";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { getErrorMessage } from "@impero-com/common/utils/sms";
import { Button, Alert } from "@impero-com/ui";
import { FormInstance, InputRef, Form, Spin, Input } from "antd";

import { usePostSendPin } from "api/generated/two-factor/two-factor";

import { ENVIRONMENT } from "../../env";

import * as theme from "./theme.css";

interface PinCodeValues {
    pin1: string;
    pin2: string;
    pin3: string;
    pin4: string;
    captcha?: string;
}

interface PinCodeFormInternalProps {
    form: FormInstance<PinCodeValues>;
    handleSubmit: (pin: PinCodeValues) => void;
    resetError: () => void;
    pinError: string | undefined;
    requiresHumanVerification: boolean;
}

type PinCodeOnKeyUpNextAction =
    | { type: "FOCUS_NEXT"; next: React.RefObject<InputRef | null> }
    | { type: "DONE" };

const PinCodeFormInternal: React.FC<PinCodeFormInternalProps> = (
    props: PinCodeFormInternalProps
) => {
    const pinInput = React.useRef<InputRef>(null);
    const pinInputTwo = React.useRef<InputRef>(null);
    const pinInputThree = React.useRef<InputRef>(null);
    const pinInputFour = React.useRef<InputRef>(null);
    const captchaRef = React.useRef<HCaptcha>(null);

    const handlePinInput = (
        e: React.KeyboardEvent<HTMLInputElement>,
        nextInput: PinCodeOnKeyUpNextAction
    ) => {
        if (/^[a-z0-9]$/i.test(e.key)) {
            props.resetError();
            switch (nextInput.type) {
                case "DONE":
                    props.form.submit();
                    break;
                case "FOCUS_NEXT":
                    nextInput.next.current!.focus();
                    break;
                default:
                    assertNever(nextInput);
            }
        }
    };

    const { getFieldValue } = props.form;
    const pin1 = getFieldValue("pin1");
    const pin2 = getFieldValue("pin2");
    const pin3 = getFieldValue("pin3");
    const pin4 = getFieldValue("pin4");

    const validatePin = (_rule: any, _value: any): Promise<void> => {
        if (
            pin1 === undefined ||
            pin2 === undefined ||
            pin3 === undefined ||
            pin4 === undefined
        ) {
            return Promise.reject(new Error(t("Login_PinCodeOrRecoveryCode")));
        } else {
            return Promise.resolve();
        }
    };

    // Ensures that if the form is empty, we put the user's focus on the first input
    React.useEffect(() => {
        if (
            (pin1 === undefined &&
                pin2 === undefined &&
                pin3 === undefined &&
                pin4 === undefined) ||
            props.pinError !== undefined
        ) {
            pinInput.current!.focus();
        }
    }, [pin1, pin2, pin3, pin4, props.pinError]);

    // The pin is submitted as soon as it is fully typed, so a captcha solved afterwards submits
    // the form as well
    const handleCaptchaVerify = (token: string) => {
        props.form.setFieldValue("captcha", token);
        const values = props.form.getFieldsValue();
        if (values.pin1 && values.pin2 && values.pin3 && values.pin4) {
            props.form.submit();
        }
    };

    const handleFinish = (values: PinCodeValues) => {
        // Captchas are one-time-usage
        captchaRef.current?.resetCaptcha();
        props.handleSubmit(values);
    };

    const inputStatus = props.pinError !== undefined ? "error" : "";
    return (
        <Form
            onFinish={handleFinish}
            className={theme.pinCodeForm}
            form={props.form}
        >
            <Form.Item rules={[{ validator: validatePin }]}>
                <Input.Group className={theme.pinCodeInputGroup}>
                    <Form.Item name="pin1">
                        <Input
                            status={inputStatus}
                            className={theme.pinInput}
                            maxLength={1}
                            onKeyUp={(e) =>
                                handlePinInput(e, {
                                    type: "FOCUS_NEXT",
                                    next: pinInputTwo,
                                })
                            }
                            ref={pinInput}
                        />
                    </Form.Item>
                    <Form.Item name="pin2">
                        <Input
                            status={inputStatus}
                            className={theme.pinInput}
                            maxLength={1}
                            onKeyUp={(e) =>
                                handlePinInput(e, {
                                    type: "FOCUS_NEXT",
                                    next: pinInputThree,
                                })
                            }
                            ref={pinInputTwo}
                        />
                    </Form.Item>
                    <Form.Item name="pin3">
                        <Input
                            status={inputStatus}
                            className={theme.pinInput}
                            maxLength={1}
                            onKeyUp={(e) =>
                                handlePinInput(e, {
                                    type: "FOCUS_NEXT",
                                    next: pinInputFour,
                                })
                            }
                            ref={pinInputThree}
                        />
                    </Form.Item>
                    <Form.Item name="pin4">
                        <Input
                            status={inputStatus}
                            className={theme.pinInput}
                            maxLength={1}
                            onKeyUp={(e) => handlePinInput(e, { type: "DONE" })}
                            ref={pinInputFour}
                        />
                    </Form.Item>
                </Input.Group>
            </Form.Item>
            {props.requiresHumanVerification ? (
                <Form.Item
                    name="captcha"
                    rules={[{ required: true }]}
                    className={theme.hcaptchaContainer}
                >
                    <HCaptcha
                        size="normal"
                        sitekey={ENVIRONMENT.IMPERO_HCAPTCHA_SITE_KEY}
                        onVerify={handleCaptchaVerify}
                        ref={captchaRef}
                    />
                </Form.Item>
            ) : null}
        </Form>
    );
};

interface SMSStatusProps {
    errorMessage: string | undefined;
    showLoading: boolean;
}

const SendSMSStatus: React.FC<SMSStatusProps> = (props: SMSStatusProps) => {
    const errorAlert =
        props.errorMessage !== undefined ? (
            <Alert message={props.errorMessage} type="error" />
        ) : null;
    const spinner = props.showLoading ? <Spin /> : null;

    return (
        <>
            {errorAlert}
            {spinner}
        </>
    );
};

interface PinCodeValidationError {
    type: "ERROR";
    error: string;
    requiresHumanVerification: boolean;
}
interface PinCodeValidationSuccess {
    type: "SUCCESS";
}
type PinCodeValidationResult =
    | PinCodeValidationSuccess
    | PinCodeValidationError;

interface PinCodeFormTwoProps {
    onSubmit: (
        pin: string,
        captcha: string | null
    ) => Promise<PinCodeValidationResult>;
}

const PinCodeFormTwo: React.FC<PinCodeFormTwoProps> = (
    props: PinCodeFormTwoProps
) => {
    const [pinError, setPinError] = React.useState<string | undefined>(
        undefined
    );
    const [requiresHumanVerification, setRequiresHumanVerification] =
        React.useState<boolean>(false);
    const [form] = Form.useForm<PinCodeValues>();

    const sendSMS = usePostSendPin();

    const computeSmsStatusProps = (): SMSStatusProps => {
        switch (sendSMS.status) {
            case "error":
                const errorMessageFromHeader = sendSMS.error.response?.headers[
                    "x-impero-smserrorstatus"
                ] as string;
                const displayErrorMessage =
                    errorMessageFromHeader !== undefined
                        ? getErrorMessage(errorMessageFromHeader)
                        : t("SMS_UnknownError");
                return {
                    errorMessage: displayErrorMessage,
                    showLoading: false,
                };
            case "idle":
            case "pending":
                return {
                    errorMessage: pinError,
                    showLoading: true,
                };
            case "success":
                return {
                    errorMessage: pinError,
                    showLoading: false,
                };
            default:
                return assertNever(sendSMS);
        }
    };

    const resendSMS = () => {
        form.resetFields();
        sendSMS.mutate({ data: { challenge: null } });
    };

    const handleSubmit = (pin: PinCodeValues) => {
        props
            .onSubmit(
                pin.pin1 + pin.pin2 + pin.pin3 + pin.pin4,
                pin.captcha ?? null
            )
            .then((result) => {
                switch (result.type) {
                    case "ERROR":
                        resendSMS();
                        setPinError(result.error);
                        if (result.requiresHumanVerification) {
                            setRequiresHumanVerification(true);
                        }
                        break;
                    case "SUCCESS":
                        // Irrelevant here : the parent component should do something with the result
                        break;
                    default:
                        assertNever(result);
                }
            });
    };

    const resetError = () => {
        setPinError(undefined);
    };

    // On the first display of this component, we ask the server for an SMS pin
    const askServerForSMS = sendSMS.mutate;
    React.useEffect(() => {
        askServerForSMS({ data: { challenge: null } });
    }, [askServerForSMS]);

    const { errorMessage, showLoading } = computeSmsStatusProps();

    return (
        <div className={theme.pinCodeContainer}>
            <PinCodeFormInternal
                form={form}
                resetError={resetError}
                handleSubmit={handleSubmit}
                pinError={pinError}
                requiresHumanVerification={requiresHumanVerification}
            />
            <SendSMSStatus
                errorMessage={errorMessage}
                showLoading={showLoading}
            />
            <Button
                id="resendpin-link"
                variant="link"
                onClick={() => {
                    resendSMS();
                    setPinError(undefined);
                }}
                disabled={sendSMS.isPending || sendSMS.isIdle}
            >
                {t("Login_ResendPIN")}
            </Button>
        </div>
    );
};

export { PinCodeFormTwo as PinCodeForm, type PinCodeValidationResult };
